import { Buffer } from 'buffer';
import { xray } from 'lib/xray';
import { PdfAttachInfoAPICall } from 'reactApp/api/PdfAttachInfoAPICall';
import { PdfAttachViewPartAPICall } from 'reactApp/api/PdfAttachViewPartAPICall';
import { PdfInfoAPICall } from 'reactApp/api/PdfInfoAPICall';
import { PdfViewPartAPICall } from 'reactApp/api/PdfViewPartAPICall';
import { OvidiusPartsInfo } from 'reactApp/modules/ovidius/ovidius.types';
import { CloudFile, EStorageType } from 'reactApp/modules/storage/storage.types';
import { parse } from 'reactApp/utils/parseMultipartData';

const getRadarPrefix = (isAttach: boolean): string => (isAttach ? 'attch_' : '');

const getPdfFilePartCallback = (url: string, storage: EStorageType, file: CloudFile, fullContent: ArrayBuffer | null, isAttach = false) => {
    return async (partId: string) => {
        const radarPrefix = getRadarPrefix(isAttach);
        try {
            const { data } = isAttach
                ? await new PdfAttachViewPartAPICall().makeRequest(fullContent, null, {
                      part_id: partId,
                      filename: file.name,
                  })
                : await new PdfViewPartAPICall().makeRequest({
                      [storage]: url,
                      part_id: partId,
                  });

            xray.send(`ovidius_${radarPrefix}get_part_success`);

            return { content: Buffer.from(data) };
        } catch (error: any) {
            xray.send(`ovidius_${radarPrefix}get_part_inf_error`, {
                rlog: 'cloud_norm_pdf_err',
                rlog_message: {
                    message: 'get_part_inf_error',
                    error,
                    partId,
                    storage,
                    isAttach,
                },
            });
            return {};
        }
    };
};

const getPdfFileParts = async (
    url: string,
    storage: EStorageType,
    file: CloudFile,
    fullContent: ArrayBuffer | null,
    isAttach: boolean
): Promise<{ partsInfo?: OvidiusPartsInfo; firstPart?: ArrayBuffer }> => {
    const radarPrefix = getRadarPrefix(isAttach);
    try {
        const { headers, data } = isAttach
            ? await new PdfAttachInfoAPICall().makeRequest(fullContent, null, { filename: file.name })
            : await new PdfInfoAPICall().makeRequest({ [storage]: url });

        const contentType = headers['content-type'];
        if (!contentType) {
            xray.send(`ovidius_${radarPrefix}parts_no_content_type`, {
                rlog: 'cloud_norm_pdf_err',
                rlog_message: {
                    error: 'no_content_type',
                    contentType,
                    file: file.name,
                },
            });
            return {};
        }

        const boundary = contentType?.split('boundary=')?.[1];
        const buffer = Buffer.from(data);
        const parts = parse(buffer, boundary);

        if (parts.length !== 2) {
            xray.send(`ovidius_${radarPrefix}parts_format_error`, {
                rlog: 'cloud_norm_pdf_err',
                rlog_message: {
                    error: 'parts_format_error',
                    parts: parts.length,
                    file: file.name,
                },
            });
            return {};
        }

        const partsInfoData = parts[0]?.data;
        const partsString = new TextDecoder().decode(partsInfoData);
        const partsInfo = JSON.parse(partsString);

        const firstPart = parts[1]?.data;

        xray.send(`ovidius_${radarPrefix}parts_success`);

        return { partsInfo, firstPart };
    } catch (error: any) {
        xray.send(`ovidius_${radarPrefix}get_parts_error`, {
            rlog: 'cloud_norm_pdf_err',
            rlog_message: {
                message: 'get_parts_error',
                error,
            },
        });
        return {};
    }
};

export const Ovidius = {
    getPdfFilePartCallback,
    getPdfFileParts,
};
